"use client";

import { c as _c } from "react/compiler-runtime";
import { Film, ImagePlus, Loader2, Music, Plus, Sparkles, X } from "lucide-react";
import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { findVideoSize, getVideoOrientation, getVideoOrientationLabel, getVideoOrientations, getVideoResolution, getVideoResolutionLabel, getVideoResolutions, getVideoSizeDimensions, type VideoDuration, type VideoFrameInputs, type VideoInputImage, type VideoOrientation, type VideoResolution, type VideoSize } from "@/lib/video-gen";
interface VideoControlsProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  selectedModels: string[];
  videoSize: VideoSize;
  setVideoSize: (value: VideoSize) => void;
  videoDuration: VideoDuration;
  setVideoDuration: (value: VideoDuration) => void;
  audioEnabled: boolean;
  setAudioEnabled: (value: boolean) => void;
  audioToggleDisabled: boolean;
  audioToggleDisabledReason?: string;
  canUseFrameInputs: boolean;
  canUseEndFrameInputs: boolean;
  canUseReferenceInputs: boolean;
  canUseReferenceVideoInputs: boolean;
  canUseReferenceAudioInputs: boolean;
  frameInputs: VideoFrameInputs;
  setFrameInputs: Dispatch<SetStateAction<VideoFrameInputs>>;
  referenceImages: VideoInputImage[];
  setReferenceImages: Dispatch<SetStateAction<VideoInputImage[]>>;
  referenceVideos: string[];
  setReferenceVideos: Dispatch<SetStateAction<string[]>>;
  referenceAudios: string[];
  setReferenceAudios: Dispatch<SetStateAction<string[]>>;
  supportedVideoSizes: VideoSize[];
  supportedVideoDurations: VideoDuration[];
  // Estimated cost of one generation across the models it will run on; null
  // when a selected model has no eligible mapping or no pricing.
  estimatedCostUsd: number | null;
  isGenerating: boolean;
  onGenerate: () => void;
  imageInputRequired?: boolean;
}
type UploadTarget = "frame-start" | "frame-end" | "reference";
function formatEstimate(value: number): string {
  return value > 0 && value < 0.01 ? "<$0.01" : `$${value.toFixed(2)}`;
}
export function VideoControls(t0) {
  const $ = _c(175);
  const {
    prompt,
    setPrompt,
    selectedModels,
    videoSize,
    setVideoSize,
    videoDuration,
    setVideoDuration,
    audioEnabled,
    setAudioEnabled,
    audioToggleDisabled,
    audioToggleDisabledReason,
    canUseFrameInputs,
    canUseEndFrameInputs,
    canUseReferenceInputs,
    canUseReferenceVideoInputs,
    canUseReferenceAudioInputs,
    frameInputs,
    setFrameInputs,
    referenceImages,
    setReferenceImages,
    referenceVideos,
    setReferenceVideos,
    referenceAudios,
    setReferenceAudios,
    supportedVideoSizes,
    supportedVideoDurations,
    estimatedCostUsd,
    isGenerating,
    onGenerate,
    imageInputRequired
  } = t0;
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadTarget, setUploadTarget] = useState("frame-start");
  const [referenceVideoDraft, setReferenceVideoDraft] = useState("");
  const resolution = getVideoResolution(videoSize);
  const orientation = getVideoOrientation(videoSize);
  const resolutions = getVideoResolutions(supportedVideoSizes);
  if (!resolutions.includes(resolution)) {
    resolutions.push(resolution);
  }
  const orientations = getVideoOrientations(supportedVideoSizes, resolution);
  if (!orientations.includes(orientation)) {
    orientations.push(orientation);
  }
  let t1;
  if ($[0] !== supportedVideoDurations || $[1] !== videoDuration) {
    t1 = supportedVideoDurations.includes(videoDuration) ? supportedVideoDurations : [...supportedVideoDurations, videoDuration].sort(_temp);
    $[0] = supportedVideoDurations;
    $[1] = videoDuration;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const durations = t1;
  const selectResolution = next => {
    const size = findVideoSize(supportedVideoSizes, next, orientation) ?? findVideoSize(supportedVideoSizes, next, orientation === "landscape" ? "portrait" : "landscape");
    if (size) {
      setVideoSize(size);
    }
  };
  const selectOrientation = next_0 => {
    const size_0 = findVideoSize(supportedVideoSizes, resolution, next_0);
    if (size_0) {
      setVideoSize(size_0);
    }
  };
  let t2;
  if ($[3] !== referenceVideoDraft || $[4] !== setFrameInputs || $[5] !== setReferenceVideos) {
    t2 = () => {
      const url = referenceVideoDraft.trim();
      if (!url || !/^https:\/\//i.test(url)) {
        return;
      }
      setFrameInputs({
        start: null,
        end: null
      });
      setReferenceVideos(prev => prev.length >= 3 || prev.includes(url) ? prev : [...prev, url]);
      setReferenceVideoDraft("");
    };
    $[3] = referenceVideoDraft;
    $[4] = setFrameInputs;
    $[5] = setReferenceVideos;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const addReferenceVideo = t2;
  let t3;
  if ($[7] !== setReferenceVideos) {
    t3 = index => {
      setReferenceVideos(prev_0 => prev_0.filter((_, i) => i !== index));
    };
    $[7] = setReferenceVideos;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const removeReferenceVideo = t3;
  const [referenceAudioDraft, setReferenceAudioDraft] = useState("");
  let t4;
  if ($[9] !== referenceAudioDraft || $[10] !== setFrameInputs || $[11] !== setReferenceAudios) {
    t4 = () => {
      const url_0 = referenceAudioDraft.trim();
      if (!url_0 || !/^https:\/\//i.test(url_0)) {
        return;
      }
      setFrameInputs({
        start: null,
        end: null
      });
      setReferenceAudios(prev_1 => prev_1.length >= 3 || prev_1.includes(url_0) ? prev_1 : [...prev_1, url_0]);
      setReferenceAudioDraft("");
    };
    $[9] = referenceAudioDraft;
    $[10] = setFrameInputs;
    $[11] = setReferenceAudios;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const addReferenceAudio = t4;
  let t5;
  if ($[13] !== setReferenceAudios) {
    t5 = index_0 => {
      setReferenceAudios(prev_2 => prev_2.filter((__0, i_0) => i_0 !== index_0));
    };
    $[13] = setReferenceAudios;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const removeReferenceAudio = t5;
  const hasImageInput = !!frameInputs.start;
  const missingRequiredImage = imageInputRequired && !hasImageInput;
  let t6;
  if ($[15] !== missingRequiredImage || $[16] !== prompt || $[17] !== selectedModels.length) {
    t6 = prompt.trim().length > 0 && selectedModels.length > 0 && !missingRequiredImage;
    $[15] = missingRequiredImage;
    $[16] = prompt;
    $[17] = selectedModels.length;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const canGenerate = t6;
  const canAcceptInput = canUseFrameInputs || canUseReferenceInputs;
  const canAddEndFrame = canUseEndFrameInputs && !!frameInputs.start;
  const endFrameDisabledReason = !canUseFrameInputs ? "Frame input not supported by selected model" : !canUseEndFrameInputs ? "The selected model only accepts a first frame" : !frameInputs.start ? "Add a first frame before a last frame" : undefined;
  const defaultUploadTarget = !canUseReferenceInputs ? frameInputs.start && canAddEndFrame ? "frame-end" : "frame-start" : !canUseFrameInputs || uploadTarget === "reference" || referenceImages.length > 0 ? "reference" : frameInputs.start && canAddEndFrame ? "frame-end" : "frame-start";
  let t7;
  if ($[19] !== canAcceptInput || $[20] !== canAddEndFrame || $[21] !== canUseFrameInputs || $[22] !== canUseReferenceInputs || $[23] !== defaultUploadTarget || $[24] !== setFrameInputs || $[25] !== setReferenceAudios || $[26] !== setReferenceImages || $[27] !== setReferenceVideos) {
    t7 = (file, explicitTarget) => {
      if (!canAcceptInput || !file.type.startsWith("image/")) {
        return;
      }
      const target = explicitTarget ?? defaultUploadTarget;
      if ((target === "frame-start" || target === "frame-end") && !canUseFrameInputs) {
        return;
      }
      if (target === "frame-end" && !canAddEndFrame) {
        return;
      }
      if (target === "reference" && !canUseReferenceInputs) {
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const nextImage = {
          dataUrl: String(reader.result ?? ""),
          mediaType: file.type
        };
        if (target === "frame-start") {
          setReferenceImages([]);
          setReferenceVideos([]);
          setReferenceAudios([]);
          setFrameInputs(prev_3 => ({
            ...prev_3,
            start: nextImage
          }));
          return;
        }
        if (target === "frame-end") {
          setReferenceImages([]);
          setReferenceVideos([]);
          setReferenceAudios([]);
          setFrameInputs(prev_4 => ({
            ...prev_4,
            end: nextImage
          }));
          return;
        }
        setFrameInputs({
          start: null,
          end: null
        });
        setReferenceImages(prev_5 => {
          if (prev_5.length >= 3) {
            return prev_5;
          }
          return [...prev_5, nextImage];
        });
      };
      reader.readAsDataURL(file);
    };
    $[19] = canAcceptInput;
    $[20] = canAddEndFrame;
    $[21] = canUseFrameInputs;
    $[22] = canUseReferenceInputs;
    $[23] = defaultUploadTarget;
    $[24] = setFrameInputs;
    $[25] = setReferenceAudios;
    $[26] = setReferenceImages;
    $[27] = setReferenceVideos;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const addImageFile = t7;
  let t8;
  if ($[29] !== canGenerate || $[30] !== isGenerating || $[31] !== onGenerate) {
    t8 = e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (canGenerate && !isGenerating) {
          onGenerate();
        }
      }
    };
    $[29] = canGenerate;
    $[30] = isGenerating;
    $[31] = onGenerate;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const handleKeyDown = t8;
  let t9;
  if ($[33] !== addImageFile || $[34] !== referenceImages.length || $[35] !== uploadTarget) {
    t9 = e_0 => {
      const files = Array.from(e_0.target.files ?? []);
      for (const file_0 of files) {
        if (uploadTarget === "reference" && referenceImages.length >= 3) {
          break;
        }
        addImageFile(file_0, uploadTarget);
        if (uploadTarget !== "reference") {
          break;
        }
      }
      e_0.target.value = "";
    };
    $[33] = addImageFile;
    $[34] = referenceImages.length;
    $[35] = uploadTarget;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  const handleFileSelect = t9;
  let t10;
  if ($[37] !== addImageFile || $[38] !== canAcceptInput) {
    t10 = e_1 => {
      if (!canAcceptInput) {
        return;
      }
      const items = Array.from(e_1.clipboardData.items);
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e_1.preventDefault();
          const file_1 = item.getAsFile();
          if (file_1) {
            addImageFile(file_1);
          }
          break;
        }
      }
    };
    $[37] = addImageFile;
    $[38] = canAcceptInput;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  const handlePaste = t10;
  let t11;
  if ($[40] !== canAcceptInput) {
    t11 = e_2 => {
      e_2.preventDefault();
      e_2.stopPropagation();
      if (canAcceptInput) {
        setIsDragging(true);
      }
    };
    $[40] = canAcceptInput;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  const handleDragOver = t11;
  let t12;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e_3 => {
      e_3.preventDefault();
      e_3.stopPropagation();
      if (dropZoneRef.current && !dropZoneRef.current.contains(e_3.relatedTarget as Node)) {
        setIsDragging(false);
      }
    };
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  const handleDragLeave = t12;
  let t13;
  if ($[43] !== addImageFile || $[44] !== canAcceptInput || $[45] !== defaultUploadTarget) {
    t13 = e_4 => {
      e_4.preventDefault();
      e_4.stopPropagation();
      setIsDragging(false);
      if (!canAcceptInput) {
        return;
      }
      const files_0 = Array.from(e_4.dataTransfer.files);
      for (const file_2 of files_0) {
        if (file_2.type.startsWith("image/")) {
          addImageFile(file_2, defaultUploadTarget);
          if (defaultUploadTarget !== "reference") {
            break;
          }
        }
      }
    };
    $[43] = addImageFile;
    $[44] = canAcceptInput;
    $[45] = defaultUploadTarget;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  const handleDrop = t13;
  let t14;
  let t15;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      const handleWindowDragEnd = () => setIsDragging(false);
      window.addEventListener("dragend", handleWindowDragEnd);
      return () => window.removeEventListener("dragend", handleWindowDragEnd);
    };
    t15 = [];
    $[47] = t14;
    $[48] = t15;
  } else {
    t14 = $[47];
    t15 = $[48];
  }
  useEffect(t14, t15);
  let t16;
  if ($[49] !== setFrameInputs) {
    t16 = target_0 => {
      setFrameInputs(prev_6 => ({
        ...prev_6,
        [target_0]: null
      }));
    };
    $[49] = setFrameInputs;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  const removeFrameImage = t16;
  let t17;
  if ($[51] !== setReferenceImages) {
    t17 = index_1 => {
      setReferenceImages(prev_7 => prev_7.filter((__1, i_1) => i_1 !== index_1));
    };
    $[51] = setReferenceImages;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  const removeReferenceImage = t17;
  let t18;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = target_1 => {
      setUploadTarget(target_1);
      fileInputRef.current?.click();
    };
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  const openFilePicker = t18;
  const videoCount = selectedModels.length;
  let t19;
  if ($[54] !== estimatedCostUsd || $[55] !== videoCount || $[56] !== videoDuration || $[57] !== videoSize) {
    t19 = estimatedCostUsd === null ? undefined : `Estimated cost for ${videoCount} video${videoCount === 1 ? "" : "s"} at ${getVideoSizeDimensions(videoSize)} for ${videoDuration}s. The actual charge is settled when the job finishes.`;
    $[54] = estimatedCostUsd;
    $[55] = videoCount;
    $[56] = videoDuration;
    $[57] = videoSize;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  const estimateTitle = t19;
  const t20 = `rounded-md border-input border dark:bg-input/30 shadow-xs focus-within:ring-1 focus-within:ring-ring transition-colors ${isDragging ? "border-primary bg-primary/5 ring-1 ring-primary" : ""}`;
  let t21;
  if ($[59] !== canAcceptInput || $[60] !== isDragging) {
    t21 = isDragging && canAcceptInput && <div className="flex items-center justify-center py-4 text-sm text-muted-foreground"><ImagePlus className="mr-2 h-4 w-4" />Drop image here</div>;
    $[59] = canAcceptInput;
    $[60] = isDragging;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== frameInputs.end || $[63] !== frameInputs.start || $[64] !== removeFrameImage) {
    t22 = (frameInputs.start ?? frameInputs.end) && <div className="flex flex-wrap gap-2 px-3 pt-3">{[{
        key: "start" as const,
        label: "First",
        image: frameInputs.start
      }, {
        key: "end" as const,
        label: "Last",
        image: frameInputs.end
      }].map(item_0 => {
        if (!item_0.image) {
          return null;
        }
        return <div key={item_0.key} className="space-y-1"><div className="px-1 text-xs text-muted-foreground">{item_0.label} frame</div><div className="group relative h-14 w-14 rounded-md border"><img src={item_0.image.dataUrl} alt={`${item_0.label} frame`} className="size-full rounded-md object-cover" /><button type="button" aria-label={`Remove ${item_0.label} frame`} onClick={() => removeFrameImage(item_0.key)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"><X className="h-3 w-3" /></button></div></div>;
      })}</div>;
    $[62] = frameInputs.end;
    $[63] = frameInputs.start;
    $[64] = removeFrameImage;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== referenceImages || $[67] !== removeReferenceImage) {
    t23 = referenceImages.length > 0 && <div className="flex flex-wrap gap-2 px-3 pt-3">{referenceImages.map((image, index_2) => <div key={`${image.dataUrl}-${index_2}`} className="space-y-1"><div className="px-1 text-xs text-muted-foreground">Reference {index_2 + 1}</div><div className="group relative h-14 w-14 rounded-md border"><img src={image.dataUrl} alt={`Reference ${index_2 + 1}`} className="size-full rounded-md object-cover" /><button type="button" aria-label="Remove reference image" onClick={() => removeReferenceImage(index_2)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"><X className="h-3 w-3" /></button></div></div>)}</div>;
    $[66] = referenceImages;
    $[67] = removeReferenceImage;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== referenceVideos || $[70] !== removeReferenceVideo) {
    t24 = referenceVideos.length > 0 && <div className="flex flex-wrap gap-2 px-3 pt-3">{referenceVideos.map((url_1, index_3) => <div key={`${url_1}-${index_3}`} className="group relative flex max-w-[220px] items-center gap-1.5 rounded-md border bg-muted/40 py-1 pl-2 pr-6 text-xs" title={url_1}><Film className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="truncate">Reference video {index_3 + 1}</span><button type="button" aria-label="Remove reference video" onClick={() => removeReferenceVideo(index_3)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"><X className="h-3 w-3" /></button></div>)}</div>;
    $[69] = referenceVideos;
    $[70] = removeReferenceVideo;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== referenceAudios || $[73] !== removeReferenceAudio) {
    t25 = referenceAudios.length > 0 && <div className="flex flex-wrap gap-2 px-3 pt-3">{referenceAudios.map((url_2, index_4) => <div key={`${url_2}-${index_4}`} className="group relative flex max-w-[220px] items-center gap-1.5 rounded-md border bg-muted/40 py-1 pl-2 pr-6 text-xs" title={url_2}><Music className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="truncate">Reference audio {index_4 + 1}</span><button type="button" aria-label="Remove reference audio" onClick={() => removeReferenceAudio(index_4)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"><X className="h-3 w-3" /></button></div>)}</div>;
    $[72] = referenceAudios;
    $[73] = removeReferenceAudio;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] !== setPrompt) {
    t26 = e_5 => setPrompt(e_5.target.value);
    $[75] = setPrompt;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  let t27;
  if ($[77] !== handleKeyDown || $[78] !== handlePaste || $[79] !== isGenerating || $[80] !== prompt || $[81] !== t26) {
    t27 = <Textarea ref={textareaRef} value={prompt} onChange={t26} onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder="Describe the video you want to generate... (optionally paste or drop a first/last frame or reference image)" className="min-h-[80px] max-h-[200px] resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent" disabled={isGenerating} />;
    $[77] = handleKeyDown;
    $[78] = handlePaste;
    $[79] = isGenerating;
    $[80] = prompt;
    $[81] = t26;
    $[82] = t27;
  } else {
    t27 = $[82];
  }
  let t28;
  if ($[83] !== handleDragOver || $[84] !== handleDrop || $[85] !== handlePaste || $[86] !== t20 || $[87] !== t21 || $[88] !== t22 || $[89] !== t23 || $[90] !== t24 || $[91] !== t25 || $[92] !== t27) {
    t28 = <div ref={dropZoneRef} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onPaste={handlePaste} className={t20}>{t21}{t22}{t23}{t24}{t25}{t27}</div>;
    $[83] = handleDragOver;
    $[84] = handleDrop;
    $[85] = handlePaste;
    $[86] = t20;
    $[87] = t21;
    $[88] = t22;
    $[89] = t23;
    $[90] = t24;
    $[91] = t25;
    $[92] = t27;
    $[93] = t28;
  } else {
    t28 = $[93];
  }
  let t29;
  if ($[94] !== handleFileSelect) {
    t29 = <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />;
    $[94] = handleFileSelect;
    $[95] = t29;
  } else {
    t29 = $[95];
  }
  let t30;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = () => openFilePicker("frame-start");
    $[96] = t30;
  } else {
    t30 = $[96];
  }
  const t31 = isGenerating || !canUseFrameInputs;
  const t32 = !canUseFrameInputs ? "Frame input not supported by selected model" : undefined;
  let t33;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <ImagePlus className="mr-1.5 h-4 w-4" />;
    $[97] = t33;
  } else {
    t33 = $[97];
  }
  const t34 = frameInputs.start ? "Replace first" : "First frame";
  let t35;
  if ($[98] !== t31 || $[99] !== t32 || $[100] !== t34) {
    t35 = <Button variant="outline" size="sm" onClick={t30} disabled={t31} title={t32}>{t33}{t34}</Button>;
    $[98] = t31;
    $[99] = t32;
    $[100] = t34;
    $[101] = t35;
  } else {
    t35 = $[101];
  }
  let t36;
  if ($[102] !== canAddEndFrame || $[103] !== endFrameDisabledReason || $[104] !== frameInputs.end || $[105] !== imageInputRequired || $[106] !== isGenerating) {
    t36 = !imageInputRequired && <Button variant="outline" size="sm" onClick={() => openFilePicker("frame-end")} disabled={isGenerating || !canAddEndFrame} title={endFrameDisabledReason}><ImagePlus className="mr-1.5 h-4 w-4" />{frameInputs.end ? "Replace last" : "Last frame"}</Button>;
    $[102] = canAddEndFrame;
    $[103] = endFrameDisabledReason;
    $[104] = frameInputs.end;
    $[105] = imageInputRequired;
    $[106] = isGenerating;
    $[107] = t36;
  } else {
    t36 = $[107];
  }
  let t37;
  if ($[108] !== canUseReferenceInputs || $[109] !== imageInputRequired || $[110] !== isGenerating || $[111] !== referenceImages.length) {
    t37 = !imageInputRequired && <Button variant="outline" size="sm" onClick={() => openFilePicker("reference")} disabled={isGenerating || !canUseReferenceInputs || referenceImages.length >= 3} title={!canUseReferenceInputs ? "Reference images not supported by selected model" : undefined}><ImagePlus className="mr-1.5 h-4 w-4" />{referenceImages.length === 0 ? "Reference" : `${referenceImages.length}/3 refs`}</Button>;
    $[108] = canUseReferenceInputs;
    $[109] = imageInputRequired;
    $[110] = isGenerating;
    $[111] = referenceImages.length;
    $[112] = t37;
  } else {
    t37 = $[112];
  }
  let t38;
  if ($[113] !== addReferenceVideo || $[114] !== canUseReferenceVideoInputs || $[115] !== isGenerating || $[116] !== referenceVideoDraft || $[117] !== referenceVideos.length) {
    t38 = canUseReferenceVideoInputs && <div className="flex items-center gap-1.5"><Input value={referenceVideoDraft} onChange={e_6 => setReferenceVideoDraft(e_6.target.value)} onKeyDown={e_7 => {
        if (e_7.key === "Enter") {
          e_7.preventDefault();
          addReferenceVideo();
        }
      }} placeholder="Reference video URL (https://)" disabled={isGenerating || referenceVideos.length >= 3} className="h-8 w-[220px] text-sm" /><Button variant="outline" size="sm" onClick={addReferenceVideo} disabled={isGenerating || referenceVideos.length >= 3 || !/^https:\/\//i.test(referenceVideoDraft.trim())} title="Add an HTTPS reference video URL"><Plus className="mr-1.5 h-4 w-4" />{referenceVideos.length === 0 ? "Video" : `${referenceVideos.length}/3 videos`}</Button></div>;
    $[113] = addReferenceVideo;
    $[114] = canUseReferenceVideoInputs;
    $[115] = isGenerating;
    $[116] = referenceVideoDraft;
    $[117] = referenceVideos.length;
    $[118] = t38;
  } else {
    t38 = $[118];
  }
  let t39;
  if ($[119] !== addReferenceAudio || $[120] !== canUseReferenceAudioInputs || $[121] !== isGenerating || $[122] !== referenceAudioDraft || $[123] !== referenceAudios.length) {
    t39 = canUseReferenceAudioInputs && <div className="flex items-center gap-1.5"><Input value={referenceAudioDraft} onChange={e_8 => setReferenceAudioDraft(e_8.target.value)} onKeyDown={e_9 => {
        if (e_9.key === "Enter") {
          e_9.preventDefault();
          addReferenceAudio();
        }
      }} placeholder="Reference audio URL (https://)" disabled={isGenerating || referenceAudios.length >= 3} className="h-8 w-[220px] text-sm" /><Button variant="outline" size="sm" onClick={addReferenceAudio} disabled={isGenerating || referenceAudios.length >= 3 || !/^https:\/\//i.test(referenceAudioDraft.trim())} title="Add an HTTPS reference audio URL"><Plus className="mr-1.5 h-4 w-4" />{referenceAudios.length === 0 ? "Audio ref" : `${referenceAudios.length}/3 audio`}</Button></div>;
    $[119] = addReferenceAudio;
    $[120] = canUseReferenceAudioInputs;
    $[121] = isGenerating;
    $[122] = referenceAudioDraft;
    $[123] = referenceAudios.length;
    $[124] = t39;
  } else {
    t39 = $[124];
  }
  let t40;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <SelectTrigger size="sm" className="min-w-[96px]" aria-label="Resolution" title="Resolution"><SelectValue placeholder="Resolution" /></SelectTrigger>;
    $[125] = t40;
  } else {
    t40 = $[125];
  }
  const T0 = Select;
  const t41 = val_0 => selectOrientation(val_0 as VideoOrientation);
  const t42 = isGenerating || orientations.length < 2;
  const t43 = orientations.length < 2 ? `Only ${getVideoOrientationLabel(orientation).toLowerCase()} is available at ${getVideoResolutionLabel(resolution)} for the selected model` : "Aspect ratio";
  let t44;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <SelectValue placeholder="Aspect ratio" />;
    $[126] = t44;
  } else {
    t44 = $[126];
  }
  let t45;
  if ($[127] !== t43) {
    t45 = <SelectTrigger size="sm" className="min-w-[120px]" aria-label="Aspect ratio" title={t43}>{t44}</SelectTrigger>;
    $[127] = t43;
    $[128] = t45;
  } else {
    t45 = $[128];
  }
  const T1 = SelectContent;
  const t46 = orientations.map(_temp3);
  let t47;
  if ($[129] !== T1 || $[130] !== t46) {
    t47 = <T1>{t46}</T1>;
    $[129] = T1;
    $[130] = t46;
    $[131] = t47;
  } else {
    t47 = $[131];
  }
  let t48;
  if ($[132] !== T0 || $[133] !== orientation || $[134] !== t41 || $[135] !== t42 || $[136] !== t45 || $[137] !== t47) {
    t48 = <T0 value={orientation} onValueChange={t41} disabled={t42}>{t45}{t47}</T0>;
    $[132] = T0;
    $[133] = orientation;
    $[134] = t41;
    $[135] = t42;
    $[136] = t45;
    $[137] = t47;
    $[138] = t48;
  } else {
    t48 = $[138];
  }
  let t49;
  if ($[139] !== videoSize) {
    t49 = getVideoSizeDimensions(videoSize);
    $[139] = videoSize;
    $[140] = t49;
  } else {
    t49 = $[140];
  }
  let t50;
  if ($[141] !== t49) {
    t50 = <span className="text-xs text-muted-foreground tabular-nums" title="Output size">{t49}</span>;
    $[141] = t49;
    $[142] = t50;
  } else {
    t50 = $[142];
  }
  const t51 = String(videoDuration);
  let t52;
  if ($[143] !== setVideoDuration) {
    t52 = val_1 => setVideoDuration(Number(val_1) as VideoDuration);
    $[143] = setVideoDuration;
    $[144] = t52;
  } else {
    t52 = $[144];
  }
  let t53;
  if ($[145] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <SelectTrigger size="sm" className="min-w-[80px]" aria-label="Duration" title="Duration"><SelectValue placeholder="Duration" /></SelectTrigger>;
    $[145] = t53;
  } else {
    t53 = $[145];
  }
  let t54;
  if ($[146] !== durations) {
    t54 = durations.map(_temp4);
    $[146] = durations;
    $[147] = t54;
  } else {
    t54 = $[147];
  }
  let t55;
  if ($[148] !== t54) {
    t55 = <SelectContent>{t54}</SelectContent>;
    $[148] = t54;
    $[149] = t55;
  } else {
    t55 = $[149];
  }
  let t56;
  if ($[150] !== isGenerating || $[151] !== t51 || $[152] !== t52 || $[153] !== t55) {
    t56 = <Select value={t51} onValueChange={t52} disabled={isGenerating}>{t53}{t55}</Select>;
    $[150] = isGenerating;
    $[151] = t51;
    $[152] = t52;
    $[153] = t55;
    $[154] = t56;
  } else {
    t56 = $[154];
  }
  const t57 = audioToggleDisabled && !isGenerating ? audioToggleDisabledReason : undefined;
  let t58;
  if ($[155] !== audioEnabled || $[156] !== audioToggleDisabled || $[157] !== setAudioEnabled) {
    t58 = <Switch checked={audioEnabled} onCheckedChange={setAudioEnabled} disabled={audioToggleDisabled} />;
    $[155] = audioEnabled;
    $[156] = audioToggleDisabled;
    $[157] = setAudioEnabled;
    $[158] = t58;
  } else {
    t58 = $[158];
  }
  let t59;
  if ($[159] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <span>Audio</span>;
    $[159] = t59;
  } else {
    t59 = $[159];
  }
  let t60;
  if ($[160] !== t57 || $[161] !== t58) {
    t60 = <label className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm" title={t57}>{t58}{t59}</label>;
    $[160] = t57;
    $[161] = t58;
    $[162] = t60;
  } else {
    t60 = $[162];
  }
  let t61;
  if ($[163] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <div className="flex-1" />;
    $[163] = t61;
  } else {
    t61 = $[163];
  }
  let t62;
  if ($[164] !== missingRequiredImage) {
    t62 = missingRequiredImage && <p className="text-sm text-destructive">This model requires a start frame image</p>;
    $[164] = missingRequiredImage;
    $[165] = t62;
  } else {
    t62 = $[165];
  }
  let t63;
  if ($[166] !== estimateTitle || $[167] !== estimatedCostUsd) {
    t63 = estimatedCostUsd !== null && <span className="text-sm text-muted-foreground tabular-nums" title={estimateTitle}>≈ {formatEstimate(estimatedCostUsd)}</span>;
    $[166] = estimateTitle;
    $[167] = estimatedCostUsd;
    $[168] = t63;
  } else {
    t63 = $[168];
  }
  const t64 = isGenerating || !canGenerate;
  let t65;
  if ($[169] !== isGenerating) {
    t65 = isGenerating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating...</> : <><Sparkles className="mr-2 h-4 w-4" />Generate</>;
    $[169] = isGenerating;
    $[170] = t65;
  } else {
    t65 = $[170];
  }
  let t66;
  if ($[171] !== onGenerate || $[172] !== t64 || $[173] !== t65) {
    t66 = <Button onClick={onGenerate} disabled={t64} className="min-w-[120px]">{t65}</Button>;
    $[171] = onGenerate;
    $[172] = t64;
    $[173] = t65;
    $[174] = t66;
  } else {
    t66 = $[174];
  }
  return <div className="border-b bg-background p-4"><div className="max-w-4xl mx-auto space-y-3">{t28}{t29}<div className="flex flex-wrap items-center gap-2">{t35}{t36}{t37}{t38}{t39}<Select value={resolution} onValueChange={val => selectResolution(val as VideoResolution)} disabled={isGenerating}>{t40}<SelectContent>{resolutions.map(_temp2)}</SelectContent></Select>{t48}{t50}{t56}{t60}{t61}{t62}{t63}{t66}</div></div></div>;
}
function _temp4(duration) {
  return <SelectItem key={duration} value={String(duration)}>{duration}s</SelectItem>;
}
function _temp3(option_0) {
  return <SelectItem key={option_0} value={option_0}>{getVideoOrientationLabel(option_0)}</SelectItem>;
}
function _temp2(option) {
  return <SelectItem key={option} value={option}>{getVideoResolutionLabel(option)}</SelectItem>;
}
function _temp(a, b) {
  return a - b;
}